import React from 'react';
import { ArrowRight, Star, Layout, PenTool } from 'lucide-react'; // Assuming you use lucide-react for icons, or use any icon lib
import img1 from "../../assets/livingroom1.jpg";
import img2 from "../../assets/livingroom2.jpeg";
import img3 from "../../assets/livingroom3.jpg";
import img4 from "../../assets/livingroom4.jpg";
import img5 from "../../assets/livingroom5.jpg";


// Placeholder interface for data
interface DesignItem {
  id: number;
  title: string;
  image: string;
  category: string;
}

const designs: DesignItem[] = [
  { id: 1, title: 'Modern Minimalism', category: 'Modern', image: img1},
  { id: 2, title: 'Classic Luxury', category: 'Classic', image: img2 },
  { id: 3, title: 'Contemporary Chic', category: 'Contemporary', image: img3 },
  { id: 4, title: 'Industrial Elegance', category: 'Industrial', image:img4 },
];

const LivingRoom = () => {
  return (
    <div className="font-sans text-gray-800">
      {/* 1. Hero Section */}
     <div
             className="relative h-[60vh] w-full bg-cover bg-center"
             style={{ backgroundImage: `url(${img5})` }}
           >
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black/40"></div>
        
        {/* Content */}
        <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-4">
          <h4 className="text-amber-500 font-bold tracking-widest uppercase mb-4">Interior Design</h4>
          <h1 className="text-5xl md:text-7xl font-serif font-bold text-white mb-6">
            Luxury Living Rooms
          </h1>
          <p className="text-gray-200 text-lg md:text-xl max-w-2xl">
            Transform your living space into a masterpiece of comfort and elegance.
          </p>
        </div>
      </div>

      {/* 2. Introduction Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-4xl font-serif font-bold text-gray-900 mb-6">
            The Heart of Your Home
          </h2>
          <div className="w-24 h-1 bg-amber-500 mx-auto mb-8"></div>
          <p className="text-gray-600 leading-relaxed text-lg">
            Your living room is more than just a place to sit; it is where life happens. 
            At <span className="font-bold text-gray-900">Luxuriya Homes</span>, we blend functionality with high-end aesthetics 
            to create living rooms that are inviting, impressive, and uniquely yours. 
            Whether you prefer the warmth of classic wood or the sleek lines of modern metal, 
            we design spaces that tell your story.
          </p>
        </div>
      </section>

      {/* 3. Gallery / Designs Grid */}
      <section className="py-20 bg-gray-50 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-end mb-12">
            <div>
              <h3 className="text-3xl font-serif font-bold text-gray-900">Curated Collections</h3>
              <p className="text-gray-500 mt-2">Explore our signature styles</p>
            </div>
            <button className="hidden md:flex items-center text-amber-600 font-semibold hover:text-amber-700 transition">
              View All Projects <ArrowRight className="ml-2 w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
            {designs.map((design) => (
              <div key={design.id} className="group relative overflow-hidden rounded-sm shadow-lg cursor-pointer">
                <div className="aspect-w-16 aspect-h-10 h-80 overflow-hidden">
                  <img 
                    src={design.image} 
                    alt={design.title} 
                    className="w-full h-full object-cover transform group-hover:scale-110 transition duration-700 ease-in-out"
                  />
                </div>
                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition duration-300 flex flex-col items-center justify-center text-center">
                  <p className="text-amber-400 text-sm tracking-widest uppercase mb-2">{design.category}</p>
                  <h4 className="text-2xl font-serif text-white">{design.title}</h4>
                </div>
              </div>
            ))}
          </div>
          
          {/* Mobile view all button */}
          <div className="mt-8 text-center md:hidden">
            <button className="text-amber-600 font-semibold inline-flex items-center">
              View All Projects <ArrowRight className="ml-2 w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* 4. Why Choose Us (Features) */}
      <section className="py-20 px-6 bg-stone-900 text-white">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mb-6 text-amber-500">
              <Star className="w-8 h-8" />
            </div>
            <h4 className="text-xl font-serif font-bold mb-3">Premium Materials</h4>
            <p className="text-gray-400">We source only the finest fabrics, woods, and metals to ensure lasting luxury.</p>
          </div>
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mb-6 text-amber-500">
              <Layout className="w-8 h-8" />
            </div>
            <h4 className="text-xl font-serif font-bold mb-3">Space Planning</h4>
            <p className="text-gray-400">Expert layouts that maximize flow, light, and utility in your living area.</p>
          </div>
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mb-6 text-amber-500">
              <PenTool className="w-8 h-8" />
            </div>
            <h4 className="text-xl font-serif font-bold mb-3">Custom Furniture</h4>
            <p className="text-gray-400">Bespoke furniture pieces designed specifically to fit your unique style.</p>
          </div>
        </div>
      </section>

      {/* 5. CTA Section */}
      <section className="py-24 px-6 bg-white text-center">
        <h2 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 mb-6">
          Ready to Redefine Your Living Space?
        </h2>
        <p className="text-lg text-gray-600 mb-10 max-w-2xl mx-auto">
          Book a consultation with our expert designers and take the first step towards your dream home.
        </p>
        <button className="bg-amber-500 hover:bg-amber-600 text-white text-lg font-semibold px-10 py-4 rounded-sm transition duration-300 shadow-xl">
          Start Your Project
        </button>
      </section>
    </div>
  );
};

export default LivingRoom;